import { useContext, useEffect, useState } from 'react';
import { UserContext } from '@services/Session';
import axios from '@helpers/axios';
import * as Sentry from '@sentry/react';
import {
  isMonetizedSport,
  isLoggedInUserEntitled,
  isPlayerPublic,
} from '@helpers/utils/monetizationUtils';
import { ViewerEntitlementStatus } from '@components/Paywall/paywall.types';
import { strogging } from '@shd/jslib/infra';
import { useIsFeatureEnabled } from '@helpers/flagUtils';

const useIsEntitledToViewPlayer = ({
  playerId,
  userId,
  selectedSport,
  teamId,
}: {
  playerId: string;
  userId: string | undefined;
  selectedSport?: string;
  teamId?: string;
}) => {
  const userStore = useContext(UserContext);
  const monetizationEnabled = useIsFeatureEnabled('vb_monetization', userStore);

  const [status, setStatus] = useState<ViewerEntitlementStatus | null>(
    ViewerEntitlementStatus.ENTITLED
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkViewEntitlement = async () => {
      // Non-BBSB sports are still free.

      if (!isMonetizedSport(selectedSport, monetizationEnabled)) {
        setStatus(ViewerEntitlementStatus.ENTITLED);
        setLoading(false);
        return;
      }

      if (!playerId && !teamId) {
        setStatus(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        let loggedInUserIsEntitled = false;

        if (userId) {
          try {
            loggedInUserIsEntitled = await isLoggedInUserEntitled();

            const isPublic = await isPlayerPublic(playerId);

            const userManagesPlayer = userStore.userPlayers.some(
              (p) => p._id === playerId
            );

            if (loggedInUserIsEntitled && userManagesPlayer) {
              strogging.log('User is entitled and manages player');
              setStatus(ViewerEntitlementStatus.ENTITLED);
              return;
            } else if (loggedInUserIsEntitled && isPublic) {
              strogging.log('User is entitled but player is public');
              setStatus(ViewerEntitlementStatus.ENTITLED);
              return;
            } else if (loggedInUserIsEntitled && !isPublic) {
              strogging.log('User is entitled but player is private');
              setStatus(ViewerEntitlementStatus.ENTITLED_BUT_PRIVATE);
              return;
            }
          } catch (e) {
            Sentry.captureException(e);
          }
        }

        if (!loggedInUserIsEntitled) {
          const response = await axios.get(
            `/api/player/${playerId}/is_entitled_to_view`
          );
          if (selectedSport !== 'bbsb' || !selectedSport) {
            setStatus(ViewerEntitlementStatus.ENTITLED);
          } else {
            setStatus(response.data.status);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setStatus(null);
      } finally {
        setLoading(false);
      }
    };

    checkViewEntitlement();
  }, [
    playerId,
    teamId,
    userId,
    selectedSport,
    userStore.userTeams,
    userStore.coachTeamIds,
    userStore.userPlayers,
    monetizationEnabled,
  ]);

  return { status, loading, error };
};
export default useIsEntitledToViewPlayer;
